// src/Services/branchService.ts
//
// Client-facing "branches" are simply warehouses that an admin has flagged with
// `displayInWebsite: true`. This service fetches those and shapes them for the
// public Branches drawer.
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

export type WeekDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const WEEK_DAYS: WeekDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export interface DayHours {
  isOpen: boolean;
  open: string;
  close: string;
}

export interface BranchOperatingHours {
  open: string;
  close: string;
  timezone: string;
  days?: Record<WeekDay, DayHours>;
}

export interface BranchAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  description: string;
  address: BranchAddress;
  phone: string;
  email: string;
  manager: string;
  operatingHours?: BranchOperatingHours;
}

const WAREHOUSES_COLLECTION = 'warehouses';

export class BranchService {
  /**
   * Fetch all warehouses flagged for public display (branches).
   * We only surface active + displayInWebsite warehouses.
   */
  static async getPublicBranches(): Promise<Branch[]> {
    try {
      const ref = collection(db, WAREHOUSES_COLLECTION);
      // Single equality filter avoids needing a composite index; we filter
      // isActive client-side.
      const q = query(ref, where('displayInWebsite', '==', true));
      const snapshot = await getDocs(q);

      const branches: Branch[] = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            name: data.name || '',
            code: data.code || '',
            description: data.description || '',
            address: {
              street: data.address?.street || '',
              city: data.address?.city || '',
              state: data.address?.state || '',
              country: data.address?.country || '',
              zipCode: data.address?.zipCode || '',
            },
            phone: data.contact?.phone || '',
            email: data.contact?.email || '',
            manager: data.contact?.manager || '',
            operatingHours: data.operatingHours as BranchOperatingHours | undefined,
            isActive: data.isActive !== false,
          };
        })
        .filter((b: any) => b.isActive)
        .map(({ isActive, ...branch }: any) => branch as Branch)
        .sort((a, b) => a.name.localeCompare(b.name));

      return branches;
    } catch (error) {
      console.error('Error fetching public branches:', error);
      throw new Error('Failed to fetch branches');
    }
  }
}
